import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import type { OrderWithBigInt } from '../../utils/eip712_utils';

export const LIMIT_ORDER_EXPIRATIONS = [
  '60',
  '1440',
  '10080',
  '43200',
  '525600',
  'custom',
] as const;

/**
 * Amount of time in minutes before a limit order expires.
 */
export type LimitOrderExpiration = (typeof LIMIT_ORDER_EXPIRATIONS)[number];

/**
 * Amount of time in minutes before a limit order expires.
 */
type LimitOrderExpirationOptions = {
  [key in LimitOrderExpiration]: string;
};

export interface LimitOrderStore {
  // getters
  /**  A `LimitOrderExpiration` object. */
  expiration: LimitOrderExpiration;
  /** Custom expiration value */
  customExpiration: Date | undefined;
  /** The current limit order trade. */
  currentOrder: OrderWithBigInt | null;

  // setters
  setExpiration: (expiration: LimitOrderExpiration) => void;
  setCustomExpiration: (customExpiration: Date) => void;
  setCurrentOrder: (order: OrderWithBigInt | null) => void;
}

/**
 * Creates a store for limit order entry state. Defaults expiration to 60 minutes
 * and the current order to null. Open-order listing and selection now live in
 * the table components (TanStack row selection + the limit-orders query cache).
 */
export const useLimitOrderStore = createWithEqualityFn(
  immer<LimitOrderStore>((set) => ({
    // getters
    expiration: '60',
    currentOrder: null,
    customExpiration: undefined,

    // setters
    setExpiration: (expiration: LimitOrderExpiration) =>
      set({ expiration, customExpiration: undefined }),
    setCustomExpiration: (customExpiration: Date) =>
      set({ customExpiration, expiration: 'custom' }),
    setCurrentOrder: (currentOrder: OrderWithBigInt | null) => set({ currentOrder }),
  })),
  shallow,
);

// Selectors
export const expirationSelectors = (state: LimitOrderStore) => ({
  expiration: state.expiration,
  customExpiration: state.customExpiration,
  setExpiration: state.setExpiration,
  setCustomExpiration: state.setCustomExpiration,
});

export const LIMIT_ORDER_EXPIRY_KEY = 'matcha-limit-order-expiry';
export const LIMIT_ORDER_MAX_EXPIRATION_DAYS = 365;

/**
 * Labels to render for each limit order expiration option.
 */
export const LIMIT_ORDER_EXPIRATION_OPTIONS: LimitOrderExpirationOptions = {
  '60': '1 hour',
  '1440': '1 day',
  '10080': '7 days',
  '43200': '30 days',
  '525600': '365 days',
  'custom': 'Custom',
};
